import { isDbtFailedStatus } from "@shared/types/dbtCauses";
import type {
  TransformationGraphDisabledNode,
  TransformationGraphNode,
  TransformationGraphTest,
} from "@shared/types/transformations";

/**
 * The Models view's grouping (ux-s11-transformations.md §3.1), pure so every rule is testable
 * without a screen.
 *
 * - Schema: the warehouse schema of the model's relation (the "bronze to silver" layer);
 * - Folder: the directory under the resource path (`models/staging/x.sql` → `staging`,
 *   `models/marts/finance/x.sql` → `marts/finance`, `models/x.sql` → "Project root");
 * - Tag: a model under each of its tags, "No tag" without one;
 * - None: one flat list.
 *
 * The default is the first of Schema, Folder, Tag that gives two or more groups, else None: for
 * a customer project with no folders and no tags and two schemas that is Schema, for the dev
 * sample project (tags, no folders, one schema) Tag.
 *
 * Rows are the project's models, seeds and snapshots, enabled or disabled in dbt. Sources are read,
 * never built: they are one group of their own, last ("Sources (read, not built)").
 */

export const MODEL_GROUP_BYS = ["schema", "folder", "tag", "none"] as const;
export type ModelGroupBy = (typeof MODEL_GROUP_BYS)[number];

export const GROUP_BY_LABELS: Record<ModelGroupBy, string> = {
  schema: "Schema",
  folder: "Folder",
  tag: "Tag",
  none: "None",
};

/** One row of the Models view: a graph node, or a node the project disables in dbt. */
export type ModelRow =
  | { kind: "node"; uniqueId: string; name: string; node: TransformationGraphNode }
  | { kind: "disabled"; uniqueId: string; name: string; node: TransformationGraphDisabledNode };

export interface ModelGroup {
  /** Stable within one grouping (the value, or a sentinel for "not reported"). */
  key: string;
  label: string;
  rows: ModelRow[];
  /**
   * Every row of the group: enabled models, seeds and snapshots AND the models dbt disables
   * (contract §6.11, UX §2.7 and §3.5: "silver · 21 models · 16 disabled in dbt", where the 21
   * includes the 16).
   */
  models: number;
  /** Rows whose last build failed. */
  failed: number;
  /** Rows the project disables in dbt. */
  disabled: number;
  /** True for the sources group (read, not built). */
  sources: boolean;
}

const BUILT_TYPES = new Set(["model", "seed", "snapshot"]);
const NOT_REPORTED = "\u0000not-reported";
const INLINED = "\u0000inlined";
export const SOURCES_GROUP_KEY = "\u0000sources";

/** The folder of a project-relative path below its first segment; "" is the project root. */
export function folderOf(path: string | null | undefined): string | null {
  if (!path) return null;
  const segments = path.split("/").filter((segment) => segment.length > 0);
  if (segments.length < 2) return null;
  return segments.slice(1, -1).join("/");
}

/** Whether a row's last build failed (the shared failed set; a disabled row never has one). */
export function rowFailed(row: ModelRow): boolean {
  return (
    row.kind === "node" &&
    Boolean(row.node.lastBuilt && isDbtFailedStatus(row.node.lastBuilt.status))
  );
}

/** #1437: the sentence for an ephemeral model, which dbt inlines into its callers and never builds. */
export const INLINED_BY_DBT_SENTENCE = "Inlined by dbt, not built on its own.";

/** Whether a row is an ephemeral model: never built on its own, so never "missing". */
export function isInlinedRow(row: ModelRow): boolean {
  return row.kind === "node" && row.node.materialized === "ephemeral";
}

function keysFor(row: ModelRow, by: Exclude<ModelGroupBy, "none">): string[] {
  const node = row.node;
  switch (by) {
    case "schema":
      if (isInlinedRow(row)) return [INLINED];
      return [node.schema ?? NOT_REPORTED];
    case "folder": {
      const folder = folderOf(node.path);
      return [folder ?? NOT_REPORTED];
    }
    case "tag":
      return node.tags && node.tags.length > 0 ? [...new Set(node.tags)] : [NOT_REPORTED];
  }
}

function labelFor(key: string, by: ModelGroupBy): string {
  if (by === "none") return "All models";
  if (key === INLINED) return "Inlined by dbt";
  if (key === NOT_REPORTED) {
    if (by === "schema") return "Schema not reported";
    return by === "folder" ? "Folder not reported" : "No tag";
  }
  if (by === "folder" && key === "") return "Project root";
  return key;
}

/** The rows of the Models view: built resources and disabled ones, by name. */
export function modelRows(
  nodes: readonly TransformationGraphNode[],
  disabled: readonly TransformationGraphDisabledNode[] = [],
): ModelRow[] {
  const rows: ModelRow[] = [
    ...nodes
      .filter((node) => BUILT_TYPES.has(node.resourceType))
      .map((node) => ({ kind: "node" as const, uniqueId: node.uniqueId, name: node.name, node })),
    ...disabled.map((node) => ({
      kind: "disabled" as const,
      uniqueId: node.uniqueId,
      name: node.name,
      node,
    })),
  ];
  return rows.sort((a, b) => a.name.localeCompare(b.name) || a.uniqueId.localeCompare(b.uniqueId));
}

function summarise(key: string, label: string, rows: ModelRow[], sources = false): ModelGroup {
  return {
    key,
    label,
    rows,
    models: rows.length,
    failed: rows.filter(rowFailed).length,
    disabled: rows.filter((row) => row.kind === "disabled").length,
    sources,
  };
}

/**
 * The groups for one grouping, in the screen's order: groups with a failure first, then A→Z,
 * the "not reported" group after the named ones, and the sources group last.
 */
export function groupModels(
  rows: readonly ModelRow[],
  by: ModelGroupBy,
  sourceNodes: readonly TransformationGraphNode[] = [],
): ModelGroup[] {
  const groups: ModelGroup[] = [];
  if (by === "none") {
    if (rows.length > 0) groups.push(summarise("all", labelFor("all", by), [...rows]));
  } else {
    const byKey = new Map<string, ModelRow[]>();
    for (const row of rows) {
      for (const key of keysFor(row, by)) byKey.set(key, [...(byKey.get(key) ?? []), row]);
    }
    const built = [...byKey.entries()].map(([key, members]) =>
      summarise(key, labelFor(key, by), members),
    );
    built.sort(
      (a, b) =>
        Number(b.failed > 0) - Number(a.failed > 0) ||
        Number(a.key === NOT_REPORTED || a.key === INLINED) -
          Number(b.key === NOT_REPORTED || b.key === INLINED) ||
        Number(a.key === INLINED) - Number(b.key === INLINED) ||
        a.label.localeCompare(b.label),
    );
    groups.push(...built);
  }
  const sources = sourceNodes.filter((node) => node.resourceType === "source");
  if (sources.length > 0) {
    groups.push(
      summarise(
        SOURCES_GROUP_KEY,
        "Sources (read, not built)",
        sources
          .map((node) => ({
            kind: "node" as const,
            uniqueId: node.uniqueId,
            name: node.name,
            node,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
        true,
      ),
    );
  }
  return groups;
}

/** How many groups a grouping gives over the built and disabled rows (sources not counted). */
function groupCount(rows: readonly ModelRow[], by: Exclude<ModelGroupBy, "none">): number {
  const keys = new Set<string>();
  for (const row of rows) for (const key of keysFor(row, by)) keys.add(key);
  return keys.size;
}

/** The first of Schema, Folder, Tag with two or more groups, else None (UX §3.1). */
export function defaultGroupBy(rows: readonly ModelRow[]): ModelGroupBy {
  for (const by of ["schema", "folder", "tag"] as const) {
    if (groupCount(rows, by) >= 2) return by;
  }
  return "none";
}

export function isModelGroupBy(value: string | null): value is ModelGroupBy {
  return value !== null && (MODEL_GROUP_BYS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------------------------- *
 * The Tests view's grouping (UX §3.2): Studio's order, which customers rely on
 * ------------------------------------------------------------------------------------- */

/** A test's result in the Tests view's words. */
export const TEST_RESULTS = [
  "passed",
  "failed",
  "warned",
  "errored",
  "skipped",
  "not_run",
] as const;
export type TestResult = (typeof TEST_RESULTS)[number];

/** dbt's test status in the view's categories. */
export function testResultOf(status: string | null | undefined): TestResult {
  switch (status) {
    case "pass":
    case "success":
    case "no-op":
      return "passed";
    case "fail":
      return "failed";
    case "warn":
      return "warned";
    case "error":
    case "runtime error":
    case "partial success":
      return "errored";
    case "skipped":
      return "skipped";
    default:
      return "not_run";
  }
}

/**
 * The one tally of a project's tests (walk 2026-10-02, D5): each test's latest result across
 * runs (`lastResult`), else the graph run's own, in the Tests view's categories. The Tests view's
 * chips and the landing's Tests fact both read it, so they cannot disagree; a test with no result
 * is `not_run`, so the categories add up to the project's count.
 */
export function testTally(
  tests: ReadonlyArray<Pick<TransformationGraphTest, "status" | "lastResult">>,
): Record<TestResult, number> {
  const out = Object.fromEntries(TEST_RESULTS.map((result) => [result, 0])) as Record<
    TestResult,
    number
  >;
  for (const test of tests) out[testResultOf(test.lastResult?.status ?? test.status)] += 1;
  return out;
}

export interface TestGroupMember<T> {
  test: T;
  result: TestResult;
}

export interface TestGroup<T> {
  key: string;
  label: string;
  kind: "model" | "custom" | "sources" | "unattributed";
  members: TestGroupMember<T>[];
  failing: number;
  warning: number;
  passing: number;
}

export const CUSTOM_SQL_GROUP = "Custom SQL checks";
export const SOURCES_TEST_GROUP = "Sources";
export const UNATTRIBUTED_GROUP = "Unattributed";

/**
 * Tests grouped the way Studio groups them (`groupTests.ts:113-173`): one group per attached model
 * A→Z, then "Custom SQL checks" (singular tests), "Sources", "Unattributed". Unhealthy groups come
 * first (failing, then warning), keeping that order within each tier.
 */
export function groupTests<
  T extends { uniqueId: string; name: string; dependsOn: string[]; kind?: string },
>(
  tests: readonly T[],
  nodes: readonly Pick<TransformationGraphNode, "uniqueId" | "name" | "resourceType">[],
  resultOf: (test: T) => TestResult,
): TestGroup<T>[] {
  const byId = new Map(nodes.map((node) => [node.uniqueId, node]));
  const groups = new Map<string, TestGroup<T>>();
  const add = (key: string, label: string, kind: TestGroup<T>["kind"], test: T): void => {
    const group = groups.get(key) ?? {
      key,
      label,
      kind,
      members: [],
      failing: 0,
      warning: 0,
      passing: 0,
    };
    const result = resultOf(test);
    group.members.push({ test, result });
    if (result === "failed" || result === "errored") group.failing += 1;
    else if (result === "warned") group.warning += 1;
    else if (result === "passed") group.passing += 1;
    groups.set(key, group);
  };
  for (const test of tests) {
    if (test.kind === "singular") {
      add("custom", CUSTOM_SQL_GROUP, "custom", test);
      continue;
    }
    const model = test.dependsOn
      .map((id) => byId.get(id))
      .find((node) => node !== undefined && node.resourceType !== "source");
    if (model) {
      add(`model:${model.uniqueId}`, model.name, "model", test);
      continue;
    }
    const onSource = test.dependsOn.some(
      (id) => byId.get(id)?.resourceType === "source" || id.startsWith("source."),
    );
    if (onSource) add("sources", SOURCES_TEST_GROUP, "sources", test);
    else add("unattributed", UNATTRIBUTED_GROUP, "unattributed", test);
  }
  const kindOrder: Record<TestGroup<T>["kind"], number> = {
    model: 0,
    custom: 1,
    sources: 2,
    unattributed: 3,
  };
  const tier = (group: TestGroup<T>): number => {
    if (group.failing > 0) return 0;
    return group.warning > 0 ? 1 : 2;
  };
  for (const group of groups.values()) {
    group.members.sort(
      (a, b) =>
        a.test.name.localeCompare(b.test.name) || a.test.uniqueId.localeCompare(b.test.uniqueId),
    );
  }
  return [...groups.values()].sort(
    (a, b) =>
      tier(a) - tier(b) || kindOrder[a.kind] - kindOrder[b.kind] || a.label.localeCompare(b.label),
  );
}
